import { z } from "zod";

import { looseArray } from "../looseParse";
import {
  ACATS_ASSET_CLASSES,
  ACATS_ASSET_POSITIONS,
  ACATS_ASSET_STATUSES,
  ALPACA_TRANSFER_STATUSES,
  CONTRA_BROKER_TYPES,
  CONTRA_TRANSFER_TYPES,
  DTCC_ACCOUNT_TYPES,
  DTCC_REJECTION_REASONS,
  DTCC_TRANSFER_STATUSES,
  DTCC_TRANSFER_TYPES,
  TRANSFER_DIRECTIONS,
} from "./acats.constants";

export const acatsTransferSchema = z.object({
  acats_id: z.string(),
  account_id: z.string(),
  account_number: z.string(),
  created_at: z.string(),
  contra_account_number: z.string(),
  contra_broker_number: z.string(),
  transfer_type: z.enum(DTCC_TRANSFER_TYPES),
  settlement_date: z.string().optional(),
  transfer_identifier: z.string().optional(),
  original_transfer_identifier: z.string().optional(),
  direction: z.enum(TRANSFER_DIRECTIONS),
  status: z.object({
    transfer_status: z.union([z.enum(ALPACA_TRANSFER_STATUSES), z.string()]),
    dtcc_status: z
      .union([z.enum(DTCC_TRANSFER_STATUSES), z.string()])
      .nullish(),
  }),
  rejection_details: z
    .object({
      rejection_reason: z.union([z.enum(DTCC_REJECTION_REASONS), z.string()]),
      rejection_message: z.string(),
    })
    .optional(),
  ip_address: z.string().optional(),
  contra_broker_name: z.string().optional(),
  contra_broker_type: z.enum(CONTRA_BROKER_TYPES).optional(),
  expires_at: z.string().optional(),
  account_name: z.string().optional(),
  account_type: z.enum(DTCC_ACCOUNT_TYPES).optional(),
});

export const getListAcatsTransfersForAccountResponseSchema = z.object({
  // Per-item, not per-array: `getAllNonCompletedAcatsTransfers` dereferences
  // `.transfers` directly, so one malformed transfer must not fail the whole
  // envelope object and fall back to a possibly-shapeless raw payload.
  transfers: looseArray(
    acatsTransferSchema,
    "getListAcatsTransfersForAccountResponseSchema.transfers",
  ),
  pending_transfers: z.number().optional(),
  page_token_next: z.string().optional(),
  page_token_previous: z.string().optional(),
});

export const acatsAssetSchema = z.object({
  isin: z.string().optional(),
  asset_class: z.enum(ACATS_ASSET_CLASSES),
  asset_description: z.string().optional(),
  symbol: z.string().optional(),
  status: z.enum(ACATS_ASSET_STATUSES),
  position: z.enum(ACATS_ASSET_POSITIONS),
  qty: z.string().optional(),
  // Monetary value: sibling money fields (e.g. cashBalanceSchema.amount) are
  // strings, so coerce to avoid dropping an asset from the looseArray if the
  // backend serializes it as a decimal string. (No live ACATS data to confirm.)
  amount: z.coerce.number().optional(),
  currency_code: z.string().optional(),
  rejection_message: z.string().optional(),
});

export const contraBrokerSchema = z.object({
  number: z.string(),
  type: z.enum(CONTRA_BROKER_TYPES),
  name: z.string(),
});

export const getListContraBrokersResponseSchema = z.object({
  brokers: looseArray(
    contraBrokerSchema,
    "getListContraBrokersResponseSchema.brokers",
  ),
});

export const contraAccountDetailsSchema = z.object({
  account_number: z.string(),
  customer_name: z.string(),
  customer_tax_id: z.string(),
  broker_number: z.string(),
  broker_name: z.string().optional(),
});

export const cashBalanceSchema = z.object({
  amount: z.string(),
  currency: z.string(),
});

export const equitySchema = z.object({
  cusip: z.string(),
  position: z.enum(ACATS_ASSET_POSITIONS),
  qty: z.string().optional(),
});

export const securitiesSchema = z.object({
  // Per-item: a single equity with an unexpected `position` enum must not drop
  // the entire incoming-securities list from the ACATS review screen.
  equities: looseArray(equitySchema, "securitiesSchema.equities").optional(),
});

export const contraTransferDetailsSchema = z.object({
  type: z.enum(CONTRA_TRANSFER_TYPES),
  cash: looseArray(
    cashBalanceSchema,
    "contraTransferDetailsSchema.cash",
  ).optional(),
  securities: securitiesSchema.optional(),
});

export const postAcatsTransferResponseSchema = z.object({
  acats_id: z.string(),
});

export const assetValidationResultSchema = z.object({
  symbol: z.string().optional(),
  cusip: z.string(),
  is_valid: z.boolean(),
  validation_issues: z.array(z.string()).optional(),
});

export const assetValidationSummarySchema = z.object({
  total_assets: z.number(),
  valid_assets: z.number(),
  invalid_assets: z.number(),
});

export const validateAcatsAssetsByCusipResponseSchema = z.object({
  validation_results: looseArray(
    assetValidationResultSchema,
    "validateAcatsAssetsByCusipResponseSchema.validation_results",
  ),
  summary: assetValidationSummarySchema,
});
